import api from "./client";


// ========== NOTAS FISCAIS ==========

export interface NotaFiscalResumo {
	nota_fiscal_id: number;
	chave_acesso: string;
	numero_nota: string;
	serie: string;
	status: string;
	valor_total: number;
	data_emissao: string;
	data_importacao: string;
	previsao_entrega: string | null;
	data_chegada: string | null;
	recebido_por?: number | null;
	data_lancamento_estoque: string | null;
	quantidade_volumes: number;
	nota_origem: string | null;
	origem_importacao: string;
	situacao_tiny: string | null;
	marcadores: string[] | null;
	tem_ocorrencia_pendente?: boolean;
	/** Nota tem item sem produto vinculado — bloqueia gerar devolução. */
	tem_produto_nao_cadastrado?: boolean;
	grupo_vinculo_id: number | null;
	numero_nota_principal: string | null;
	/**
	 * #1345: a nota é a OPERÁVEL do grupo (transferência principal) — ou não tem
	 * grupo. Satélite (remessa vinculada) é somente consulta: o backend recusa
	 * chegada/conferência/entrada nela (assertOperavel).
	 */
	is_principal?: boolean;
	/** Remessas (fornecedor) vinculadas a esta nota — preenchido na transferência. */
	remessas_vinculadas?: {
		numero_nota: string;
		razao_social: string;
		data_emissao?: string | null;
	}[];
	emitente: { cnpj: string; razao_social: string; uf: string };
	destinatario: { cnpj: string; razao_social: string; uf: string };
	cfop: { codigo: string; natureza_sefaz: string; natureza_operacao: string; classe_entrada: string };
}

export interface NotaVinculadaResumo {
	nota_fiscal_id: number;
	numero_nota: string;
	serie: string;
	status: string;
	data_emissao: string;
	emitente_razao_social: string | null;
	cfop_codigo: string | null;
	cfop_classe: string | null;
	is_principal: boolean;
}

export interface GrupoVinculoInfo {
	grupo_vinculo_id: number;
	nota_principal_id: number;
	notas: NotaVinculadaResumo[];
}


export interface NotaFiscalClienteDetalhe {
	cnpj: string;
	razao_social: string;
	nome_fantasia?: string | null;
	inscricao_estadual?: string | null;
	uf: string;
	endereco?: string | null;
	municipio?: string | null;
	cep?: string | null;
	telefone?: string | null;
	email?: string | null;
}

export interface NotaFiscalDetalhe extends NotaFiscalResumo {
	modelo: string;
	peso_bruto: number | null;
	peso_liquido: number | null;
	transportadora_nome: string | null;
	transportadora_cnpj: string | null;
	importado_por_usuario: { usuario_id: number; responsavel: string } | null;
	recebido_por_usuario: { usuario_id: number; responsavel: string } | null;
	emitente: NotaFiscalClienteDetalhe;
	destinatario: NotaFiscalClienteDetalhe;
	itens: NotaFiscalItemDetalhe[];
	volumes: NotaFiscalVolumeDetalhe[];
	eventos: NotaFiscalEventoDetalhe[];
	grupo_vinculo: GrupoVinculoInfo | null;
	/**
	 * #1303: quem entregou a nota — transportadora do agendamento, senão a
	 * transportadora declarada no XML, senão o próprio emitente (fornecedor).
	 */
	entrega_por: {
		nome: string;
		origem: "AGENDAMENTO" | "XML" | "EMITENTE";
	} | null;
	/**
	 * #1303: quem REALMENTE bipou, por tentativa de conferência. Os eventos
	 * INICIO/FIM_CONFERENCIA registram quem operou a tela (criou, resetou,
	 * finalizou) — que muitas vezes não é quem conferiu.
	 */
	conferencia_operadores: {
		tentativa: number;
		operadores: string[];
		/**
		 * Instante em que a tentativa fechou (o histórico grava N linhas com o
		 * mesmo timestamp, não bip a bip). É por ele que a timeline descobre a
		 * qual conferência a tentativa pertence — depois de um reset a numeração
		 * recomeça e deixa de casar com a ordem das conferências.
		 */
		registrado_em: string | null;
	}[];
	/**
	 * #1303: resumo de divergência da conferência para o card do ciclo de vida.
	 * `produtos` = EANs distintos que divergiram, `unidades` = soma das
	 * diferenças. Agregado no backend contando só QUANTIDADE_MAIOR/MENOR/
	 * NAO_CONSTA — `PRODUTO_NAO_CADASTRADO` fica fora. `null` quando a nota não
	 * teve divergência (ou não foi conferida).
	 */
	divergencia_conferencia: {
		produtos: number;
		unidades: number;
	} | null;
}


export interface NotaFiscalItemDetalhe {
	nota_fiscal_item_id: number;
	numero_item: number;
	codigo_produto_xml: string;
	ean: string | null;
	descricao_xml: string;
	ncm: string | null;
	unidade: string;
	quantidade: number;
	valor_unitario: number;
	valor_total: number;
	valor_desconto: number;
	produto_nao_encontrado: boolean;
	produto: { produto_id: number; descricao: string; sku: string; ean: string | null } | null;
	/**
	 * #1980 — kit: o item virou vários produtos do WMS. Vazio/ausente no item
	 * comum, que continua resolvendo por `produto`.
	 */
	componentes?: {
		produto_id: number;
		quantidade: number;
		produto: { produto_id: number; descricao: string; sku: string } | null;
	}[];
	cfop_item: { codigo: string; natureza_sefaz: string } | null;
	impostos: {
		tipo_imposto: string;
		base_calculo: number | null;
		aliquota: number | null;
		valor: number | null;
		cst: string | null;
	}[];
}


export interface NotaFiscalVolumeDetalhe {
	nota_fiscal_volume_id: number;
	numero_volume: number;
	ean: string;
	especie: string | null;
	peso_bruto: number | null;
	peso_liquido: number | null;
	origem: string;
	agregado: boolean;
}


export interface NotaFiscalEventoDetalhe {
	nota_fiscal_evento_id: number;
	tipo_evento: string;
	observacao: string | null;
	data_evento: string;
	usuario: { usuario_id: number; responsavel: string } | null;
}


export async function listarNotasFiscais(params?: {
	search?: string;
	status?: string;
	classe?: string;
	dataInicio?: string;
	dataFim?: string;
	conferencia_atrasada?: string;
	com_ocorrencia_pendente?: string;
	agendamento_fora_prazo?: boolean | string;
	/** #1901: entrega vencida sem bip da chave (marcador ATRASADA). */
	atrasada?: boolean | string;
	excluir_com_devolucao?: string;
	fornecedor_cnpj?: string;
	/** Canal de venda pelo nome — o backend resolve pelo pedido de origem. */
	canalVenda?: string;
	/** Nome do destinatário ou emitente, por trecho. */
	cliente?: string;
	/** CPF/CNPJ do destinatário ou emitente — a formatação é ignorada. */
	documento?: string;
	/** SKU, EAN ou descrição de algum item. */
	produto?: string;
	sortBy?: string;
	sortOrder?: string;
	offset?: number;
	limit?: number;
}): Promise<{
	results: NotaFiscalResumo[];
	total: number;
	// #1840: soma do valor de todas as notas do filtro (não só da página).
	valor_total?: number;
	// #1963: notas por status com os mesmos filtros, menos o próprio status.
	por_status?: Record<string, { notas: number; valor: number }>;
}> {
	try {
		const queryParams: any = {};
		if (params?.search) queryParams.search = params.search;
		if (params?.status) queryParams.status = params.status;
		if (params?.classe) queryParams.classe = params.classe;
		if (params?.dataInicio) queryParams.dataInicio = params.dataInicio;
		if (params?.dataFim) queryParams.dataFim = params.dataFim;
		if (params?.conferencia_atrasada) queryParams.conferencia_atrasada = params.conferencia_atrasada;
		if (params?.com_ocorrencia_pendente) queryParams.com_ocorrencia_pendente = params.com_ocorrencia_pendente;
		if (params?.agendamento_fora_prazo) queryParams.agendamento_fora_prazo = params.agendamento_fora_prazo;
		if (params?.atrasada) queryParams.atrasada = params.atrasada;
		if (params?.excluir_com_devolucao) queryParams.excluir_com_devolucao = params.excluir_com_devolucao;
		if (params?.fornecedor_cnpj) queryParams.fornecedor_cnpj = params.fornecedor_cnpj;
		if (params?.canalVenda) queryParams.canalVenda = params.canalVenda;
		if (params?.cliente) queryParams.cliente = params.cliente;
		if (params?.documento) queryParams.documento = params.documento;
		if (params?.produto) queryParams.produto = params.produto;
		if (params?.sortBy) queryParams.sortBy = params.sortBy;
		if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;
		queryParams.offset = params?.offset || 0;
		queryParams.limit = params?.limit || 20;
		const response = await api.get("/nota-fiscal", { params: queryParams });
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar notas fiscais:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao listar notas fiscais.");
	}
}


export interface FornecedorOption {
	cnpj: string;
	razao_social: string;
}

// #1297: fonte do autocomplete "Fornecedor" da listagem de NF.
// #1840: `classe` restringe aos emitentes da aba (sem classe / TRANSFERENCIA →
// emitentes de remessa, o fornecedor do fluxo Compras).
export async function listarFornecedores(
	search?: string,
	classe?: string,
): Promise<FornecedorOption[]> {
	try {
		const params: Record<string, string> = {};
		if (search) params.search = search;
		if (classe) params.classe = classe;
		const response = await api.get("/nota-fiscal/fornecedores", { params });
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar fornecedores:", error);
		return [];
	}
}


export async function buscarNotaFiscal(
	id: number,
): Promise<NotaFiscalDetalhe> {
	try {
		const response = await api.get(`/nota-fiscal/${id}`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar nota fiscal:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar nota fiscal.");
	}
}


export async function importarNotaFiscalXml(
	arquivo: File,
): Promise<NotaFiscalResumo> {
	try {
		const formData = new FormData();
		formData.append("arquivo", arquivo);
		const response = await api.post(
			"/nota-fiscal/importar/xml",
			formData,
			{
				headers: { "Content-Type": "multipart/form-data" },
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao importar nota fiscal:", error);
		if (error.response?.data?.message) {
			throw new Error(
				typeof error.response.data.message === "string"
					? error.response.data.message
					: JSON.stringify(error.response.data.message),
			);
		}
		if (error.response?.data?.erros) {
			throw new Error(error.response.data.erros.join("; "));
		}
		throw new Error("Falha ao importar nota fiscal.");
	}
}


export interface ImportJobStatus {
	jobId: string;
	status: "PROCESSANDO" | "CONCLUIDO" | "ERRO" | "NENHUM";
	total: number;
	processadas: number;
	importadas: number;
	ignoradas: number;
	erros: number;
	semXml: number;
	duracaoSegundos: number;
	dataInicial: string;
	dataFinal: string;
	iniciadoEm: string;
}


export async function importarNotaFiscalPorPeriodo(
	dataInicial: string,
	dataFinal: string,
): Promise<ImportJobStatus> {
	try {
		const response = await api.post("/nota-fiscal/importar/periodo", {
			dataInicial,
			dataFinal,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao importar notas por período:", error);
		if (error.response?.data?.message) {
			throw new Error(
				typeof error.response.data.message === "string"
					? error.response.data.message
					: JSON.stringify(error.response.data.message),
			);
		}
		throw new Error("Falha ao importar notas por período.");
	}
}


export type ArquivoLoteStatus = "IMPORTADA" | "IGNORADA_DUPLICADA" | "ERRO";

/** #1754 — desfecho de cada XML do ZIP. */
export interface ResultadoArquivoLote {
	/** Caminho do XML dentro do ZIP (com subpastas). */
	arquivo: string;
	chave_acesso: string | null;
	status: ArquivoLoteStatus;
	motivo: string | null;
}

/** #1754 — status do job de importação em lote por ZIP (polling). */
export interface ImportLoteStatus {
	jobId: string;
	tipo: "LOTE_ZIP";
	status: "PROCESSANDO" | "CONCLUIDO" | "ERRO";
	arquivo: string;
	total: number;
	processados: number;
	importadas: number;
	/** Duplicadas: a chave de acesso já estava importada. */
	ignoradas: number;
	erros: number;
	/** XMLs que não são NF-e (evento, cancelamento) — ignorados em silêncio. */
	ignoradosNaoNfe: number;
	mensagemErro: string | null;
	iniciadoEm: string;
	duracaoSegundos: number;
	/** Lista por arquivo — só vem quando o lote termina. */
	resultado?: ResultadoArquivoLote[];
}

/** O polling de importação acompanha os dois tipos de job: período e lote. */
export type StatusImportacao = ImportJobStatus | ImportLoteStatus;

export function ehImportacaoLote(
	job: StatusImportacao | null | undefined,
): job is ImportLoteStatus {
	return !!job && "tipo" in job && job.tipo === "LOTE_ZIP";
}

/** Mesmo teto do backend (`LIMITES_ZIP.tamanhoMaxZipBytes`). */
export const LIMITE_ZIP_IMPORTACAO_MB = 50;
export const LIMITE_XMLS_IMPORTACAO_LOTE = 500;

/**
 * #1754 — envia um ZIP com vários XMLs de NF-e. O backend valida o ZIP e
 * responde na hora com o `jobId`; o progresso vem por `consultarStatusImportacao`.
 */
export async function importarNotaFiscalLote(
	arquivo: File,
): Promise<ImportLoteStatus> {
	try {
		const formData = new FormData();
		formData.append("arquivo", arquivo);
		const response = await api.post("/nota-fiscal/importar/lote", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao importar lote de notas fiscais:", error?.message);
		// 413 do nginx vem em HTML, sem `message`: a mensagem é montada aqui.
		if (error.response?.status === 413) {
			throw new Error(
				`O ZIP passa do limite de ${LIMITE_ZIP_IMPORTACAO_MB} MB.`,
			);
		}
		if (error.response?.data?.message) {
			throw new Error(
				typeof error.response.data.message === "string"
					? error.response.data.message
					: JSON.stringify(error.response.data.message),
			);
		}
		throw new Error("Falha ao importar o lote de notas fiscais.");
	}
}


export async function consultarStatusImportacao(
	jobId: string,
): Promise<StatusImportacao> {
	const response = await api.get(`/nota-fiscal/importar/status/${jobId}`);
	return response.data;
}


export async function consultarImportacaoAtiva(): Promise<ImportJobStatus | null> {
	const response = await api.get("/nota-fiscal/importar/status");
	if (response.data.status === "NENHUM") return null;
	return response.data;
}


export async function excluirNotaFiscal(id: number): Promise<void> {
	try {
		await api.delete(`/nota-fiscal/${id}`);
	} catch (error: any) {
		console.error("Erro ao excluir nota fiscal:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao excluir nota fiscal.");
	}
}


/**
 * #1901: a chegada exige o bip da chave de acesso (44 dígitos).
 * Vale a chave da própria nota ou a de qualquer nota vinculada a ela.
 */
export async function marcarChegadaNotaFiscal(
	id: number,
	chaveAcesso: string,
): Promise<NotaFiscalDetalhe> {
	try {
		const response = await api.patch(`/nota-fiscal/${id}/marcar-chegada`, {
			chave_acesso: chaveAcesso,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao marcar chegada:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao marcar chegada da nota fiscal.");
	}
}


export async function definirPrevisaoEntregaNotaFiscal(
	id: number,
	data: string,
): Promise<NotaFiscalDetalhe> {
	try {
		const response = await api.patch(
			`/nota-fiscal/${id}/previsao-entrega`,
			{ previsao_entrega: data },
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao definir previsão de entrega:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao definir previsão de entrega.");
	}
}


// ========== AGENDAMENTOS ==========

export interface AgendamentoNota {
	nota_fiscal_id: number;
	numero_nota: string;
	fornecedor: string;
	quantidade_volumes: number;
	total_itens: number;
	status: string;
	classe_entrada: string;
}

export interface AgendamentoGrupo {
	previsao_entrega: string;
	notas: AgendamentoNota[];
}

export async function listarAgendamentos(): Promise<AgendamentoGrupo[]> {
	try {
		const response = await api.get("/nota-fiscal/agendamentos");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar agendamentos:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao listar agendamentos.");
	}
}


// ========== CFOP ==========

export interface CfopResumo {
	cfop_id: number;
	codigo: string;
	natureza_sefaz: string;
	natureza_operacao: string;
	tipo_operacao: string;
	classe_entrada: string;
}


export async function listarCfops(): Promise<CfopResumo[]> {
	try {
		const response = await api.get("/cfop");
		return response.data;
	} catch (error: any) {
		console.error("Erro ao listar CFOPs:", error);
		throw new Error("Falha ao listar CFOPs.");
	}
}


export async function atualizarCfopNotaFiscal(
	notaFiscalId: number,
	cfopId: number,
): Promise<NotaFiscalDetalhe> {
	try {
		const response = await api.patch(`/nota-fiscal/${notaFiscalId}/cfop`, {
			cfop_id: cfopId,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar CFOP:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao atualizar CFOP da nota fiscal.");
	}
}

// ========== VÍNCULO DE NOTAS ==========

/** Nota do vínculo em conflito de status (409 do backend, regra 2026-10-08). */
export interface NotaConflitoVinculo {
	nota_fiscal_id: number;
	numero_nota: string;
	status: string;
	data_chegada: string | null;
	previsao_entrega: string | null;
}

/**
 * As notas estão em status diferentes: o usuário precisa escolher de qual nota
 * vêm os dados de entrega e reenviar com `notaFonteDadosId`.
 */
export class VinculoConflitoError extends Error {
	constructor(
		message: string,
		readonly conflito: NotaConflitoVinculo[],
	) {
		super(message);
		this.name = "VinculoConflitoError";
	}
}

function lancarSeConflitoVinculo(error: any): void {
	const data = error?.response?.data;
	if (error?.response?.status === 409 && Array.isArray(data?.conflito)) {
		throw new VinculoConflitoError(data.message, data.conflito);
	}
}

export async function vincularNotasFiscais(
	notaIds: number[],
	notaPrincipalId: number,
	notaFonteDadosId?: number,
): Promise<GrupoVinculoInfo> {
	try {
		const response = await api.post("/nota-fiscal/vincular", {
			nota_ids: notaIds,
			nota_principal_id: notaPrincipalId,
			...(notaFonteDadosId != null && {
				nota_fonte_dados_id: notaFonteDadosId,
			}),
		});
		return response.data;
	} catch (error: any) {
		lancarSeConflitoVinculo(error);
		console.error("Erro ao vincular notas:", error);
		if (error.response?.data?.message) {
			throw new Error(
				typeof error.response.data.message === "string"
					? error.response.data.message
					: JSON.stringify(error.response.data.message),
			);
		}
		throw new Error("Falha ao vincular notas fiscais.");
	}
}

export async function desvincularNotaFiscal(
	notaFiscalId: number,
): Promise<void> {
	try {
		await api.delete(`/nota-fiscal/${notaFiscalId}/desvincular`);
	} catch (error: any) {
		console.error("Erro ao desvincular nota:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao desvincular nota fiscal.");
	}
}

export async function tornarNotaPrincipal(
	notaFiscalId: number,
): Promise<GrupoVinculoInfo> {
	try {
		const response = await api.patch(
			`/nota-fiscal/${notaFiscalId}/tornar-principal`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao tornar nota principal:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao tornar a nota principal.");
	}
}

export async function adicionarNotasAoGrupo(
	grupoId: number,
	notaIds: number[],
	notaFonteDadosId?: number,
): Promise<GrupoVinculoInfo> {
	try {
		const response = await api.post(
			`/nota-fiscal/grupo/${grupoId}/notas`,
			{
				nota_ids: notaIds,
				...(notaFonteDadosId != null && {
					nota_fonte_dados_id: notaFonteDadosId,
				}),
			},
		);
		return response.data;
	} catch (error: any) {
		lancarSeConflitoVinculo(error);
		console.error("Erro ao adicionar notas ao grupo:", error);
		if (error.response?.data?.message) {
			throw new Error(
				typeof error.response.data.message === "string"
					? error.response.data.message
					: JSON.stringify(error.response.data.message),
			);
		}
		throw new Error("Falha ao adicionar notas ao grupo.");
	}
}

// ========== MARCADORES ==========

export async function sincronizarMarcadoresNota(
	notaFiscalId: number,
): Promise<string[]> {
	try {
		const response = await api.patch(
			`/nota-fiscal/${notaFiscalId}/sincronizar-marcadores`,
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao sincronizar marcadores:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao sincronizar marcadores da nota fiscal.");
	}
}


// ========== DEVOLUÇÃO DE PEDIDO ==========

export interface PedidoDevolucaoResumo {
	pedido_id: number;
	num_pedido: string;
	num_pedido_ecommerce: string;
	cliente_nome: string;
	canal_venda?: string | null;
	cliente_cidade?: string;
	cliente_uf?: string;
	data: string;
	valor: string;
	situacao: number;
}

export interface GerarDevolucaoResult {
	nota_fiscal_id: number;
	numero_nota: string;
	message: string;
}

export interface ImportarPedidoTinyResult {
	sucesso: boolean;
	message: string;
	pedido?: PedidoDevolucaoResumo;
}

// GET /devolucao/buscar-pedidos?numPedido=&cliente=&canalVenda=&produto=&offset=&limit=
export async function buscarPedidosParaDevolucao(params: {
	numPedido?: string;
	cliente?: string;
	canalVenda?: string;
	produto?: string;
	documento?: string;
	offset?: number;
	limit?: number;
}): Promise<{
	results: PedidoDevolucaoResumo[];
	total: number;
}> {
	try {
		const queryParams: Record<string, string> = {};
		if (params.numPedido) queryParams.numPedido = params.numPedido;
		if (params.cliente) queryParams.cliente = params.cliente;
		if (params.canalVenda) queryParams.canalVenda = params.canalVenda;
		if (params.produto) queryParams.produto = params.produto;
		if (params.documento) queryParams.documento = params.documento;
		if (params.offset !== undefined)
			queryParams.offset = String(params.offset);
		if (params.limit !== undefined) queryParams.limit = String(params.limit);

		const response = await api.get("/devolucao/buscar-pedidos", {
			params: queryParams,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar pedidos para devolução:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar pedidos para devolução.");
	}
}

// TODO: backend — POST /nota-fiscal/devolucao/gerar-de-pedido { pedido_id }
export async function gerarDevolucaoDePedido(
	pedidoId: number,
): Promise<GerarDevolucaoResult> {
	try {
		const response = await api.post("/nota-fiscal/devolucao/gerar-de-pedido", {
			pedido_id: pedidoId,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao gerar devolução de pedido:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao gerar devolução a partir do pedido.");
	}
}

// TODO: backend — POST /nota-fiscal/devolucao/importar-pedido-tiny { numero_pedido_tiny }
export async function importarPedidoTinyParaDevolucao(
	numeroPedidoTiny: string,
): Promise<ImportarPedidoTinyResult> {
	try {
		const response = await api.post(
			"/nota-fiscal/devolucao/importar-pedido-tiny",
			{ numero_pedido_tiny: numeroPedidoTiny },
		);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao importar pedido do Tiny:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao importar pedido do Tiny.");
	}
}

